import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { PlusIcon, EditIcon, TrashIcon, CheckIcon, XIcon } from "../icons";
import EditBundleModal from "./EditBundleModal";
import PartsAndHardwareRow from "./PartsAndHardwareRow";
import Sbase from "../styles";

// This table runs to 15 columns on Vibrato Vault, and the house 12x16 cell
// padding alone costs ~450px of width — the reason the page scrolled sideways.
// Tighter cells here only; every other page keeps the global spacing.
const S = {
  ...Sbase,
  th: { ...Sbase.th, padding: "10px 8px" },
  td: { ...Sbase.td, padding: "9px 8px" },
};

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const MFG_METHODS = ["Laser Cutting", "3D Printing", "UV Printing", "CNC", "Injection Molding", "Handmade", "Other"];

// "3 Pack" / "Set of 5" / "x2" / "Single" / "Pair" → units per pack
const detectPackSize = (label) => {
  const l = (label || "").toLowerCase();
  const m = l.match(/(\d+)\s*-?\s*(?:pack|pk|pcs|pieces)\b/) || l.match(/(?:set|pack)\s+of\s+(\d+)/) || l.match(/\bx\s*(\d+)\b/);
  if (m) return parseInt(m[1], 10) || 1;
  if (/\bsingle\b/.test(l)) return 1;
  if (/\bpair\b/.test(l)) return 2;
  return 1;
};

function CellInput({ value, onChange, placeholder, type = "text", style: s }) {
  return <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ ...S.cellInput, ...s }} />;
}

function SortArrow({ sortCol, col, sortDir }) {
  if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>&#8597;</span>;
  return <span style={{ marginLeft: 4, color: "#0033FF" }}>{sortDir === "asc" ? "\u2191" : "\u2193"}</span>;
}

export default function ProductsPage({ store, storeName }) {
  const isVV = true; // base product + pack size enabled for all stores
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("");
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [addingNew, setAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({ name: "", sku: "", manufacturingMethod: "", price: "", cost: "", weightLbs: "", weightOz: "", dimensionsPacked: "", baseProduct: "", packSize: "1", filamentGrams: "", minInventory: "", targetInventory: "" });
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [editErr, setEditErr] = useState(""); // why the last inline save was refused, shown under the row
  const [selected, setSelected] = useState(new Set());
  const [batchAction, setBatchAction] = useState(null); // "baseProduct" | "packSize" | "mfgMethod" | "filamentGrams" | "delete"
  const [batchValue, setBatchValue] = useState("");
  const [batchErr, setBatchErr] = useState(""); // what the server refused in the last batch, shown in red up top
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const toggleGroup = (bp) => setExpandedGroups((prev) => { const s = new Set(prev); if (s.has(bp)) s.delete(bp); else s.add(bp); return s; });
  const [bundling, setBundling] = useState(null); // product whose contents are being edited
  const [hwByProduct, setHwByProduct] = useState({}); // product id -> [{id, qty, name, kind}]
  const [expandedParts, setExpandedParts] = useState(new Set());
  const toggleParts = (id) => setExpandedParts((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });

  const fetchProducts = useCallback(() => {
    fetch(`${API_URL}/api/products?store=${store}`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setProducts(data); })
      .catch(() => {});
  }, [store]);

  // Every link for the store in one call — the endpoint already joins the
  // hardware names, so there is nothing to fan out per row.
  const fetchHardwareLinks = useCallback(() => {
    fetch(`${API_URL}/api/product-hardware?store=${store}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const map = {};
        (Array.isArray(rows) ? rows : []).forEach((r) => {
          (map[r.productId] = map[r.productId] || []).push({
            id: r.hardwareId, qty: r.qtyPerUnit || 1, name: r.hardwareName || "Hardware", kind: "hardware",
          });
        });
        setHwByProduct(map);
      })
      .catch(() => {});
  }, [store]);

  useEffect(() => { fetchProducts(); fetchHardwareLinks(); }, [fetchProducts, fetchHardwareLinks]);

  const filtered = useMemo(() => {
    let l = products;
    if (filter) {
      const f = filter.toLowerCase();
      l = l.filter((p) =>
        p.name.toLowerCase().includes(f) || (p.sku || "").toLowerCase().includes(f) ||
        (p.manufacturingMethod || "").toLowerCase().includes(f)
      );
    }
    if (sortCol) {
      l = [...l].sort((a, b) => {
        let va = a[sortCol], vb = b[sortCol];
        if (sortCol === "price" || sortCol === "cost") { va = parseFloat(va) || 0; vb = parseFloat(vb) || 0; return sortDir === "asc" ? va - vb : vb - va; }
        va = String(va || "").toLowerCase(); vb = String(vb || "").toLowerCase();
        return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      });
    }
    return l;
  }, [products, filter, sortCol, sortDir]);

  const handleSort = (c) => {
    if (sortCol === c) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortCol(c); setSortDir("asc"); }
  };

  const addRow = () => {
    if (!newRow.name) return;
    const weightParts = [];
    if (newRow.weightLbs) weightParts.push(`${newRow.weightLbs} lbs`);
    if (newRow.weightOz) weightParts.push(`${newRow.weightOz} oz`);
    const payload = {
      store,
      name: newRow.name,
      sku: newRow.sku || "",
      manufacturingMethod: newRow.manufacturingMethod || "",
      price: newRow.price || "0",
      cost: newRow.cost || "0",
      weightPacked: weightParts.join(" ") || "",
      dimensionsPacked: newRow.dimensionsPacked || "",
    };
    if (isVV) {
      payload.baseProduct = newRow.baseProduct || "";
      payload.packSize = newRow.packSize || "1";
      payload.filamentGrams = newRow.filamentGrams || "0";
    }
    payload.minInventory = newRow.minInventory === "" ? null : newRow.minInventory;
    payload.targetInventory = newRow.targetInventory === "" ? null : newRow.targetInventory;
    fetch(`${API_URL}/api/products`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) return r.json().then((d) => { console.error("Add product error:", d); });
      fetchProducts();
      setAddingNew(false);
      setNewRow({ name: "", sku: "", manufacturingMethod: "", price: "", cost: "", weightLbs: "", weightOz: "", dimensionsPacked: "", baseProduct: "", packSize: "1" });
    }).catch((err) => console.error("Add product error:", err));
  };

  const parseWeight = (w) => {
    if (!w) return { lbs: "", oz: "" };
    const lbsMatch = w.match(/([\d.]+)\s*lbs?/i);
    const ozMatch = w.match(/([\d.]+)\s*oz/i);
    return { lbs: lbsMatch ? lbsMatch[1] : "", oz: ozMatch ? ozMatch[1] : "" };
  };

  const startEdit = (p) => {
    const { lbs, oz } = parseWeight(p.weightPacked);
    setEditId(p.id);
    setEditRow({ ...p, weightLbs: lbs, weightOz: oz });
    setEditErr(""); // a fresh edit starts clean
  };

  const saveEdit = () => {
    if (!editRow.name) return;
    const weightParts = [];
    if (editRow.weightLbs) weightParts.push(`${editRow.weightLbs} lbs`);
    if (editRow.weightOz) weightParts.push(`${editRow.weightOz} oz`);
    const payload = {
      name: editRow.name,
      sku: editRow.sku || "",
      manufacturingMethod: editRow.manufacturingMethod || "",
      price: editRow.price || "0",
      cost: editRow.cost || "0",
      weightPacked: weightParts.join(" ") || "",
      dimensionsPacked: editRow.dimensionsPacked || "",
    };
    if (isVV) {
      payload.baseProduct = editRow.baseProduct || "";
      payload.packSize = editRow.packSize || "1";
      payload.filamentGrams = editRow.filamentGrams || "0";
    }
    payload.minInventory = editRow.minInventory === "" ? null : editRow.minInventory;
    payload.targetInventory = editRow.targetInventory === "" ? null : editRow.targetInventory;
    fetch(`${API_URL}/api/products/${store}/${editId}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) {
        // Show the refusal on the row and stay in edit mode. A console-only
        // error made the save button look dead (e.g. the bundle 409).
        return r.json().then(
          (d) => setEditErr(d.error || "The server refused this change."),
          () => setEditErr("The server refused this change.")
        );
      }
      setEditErr("");
      fetchProducts(); setEditId(null);
    }).catch(() => setEditErr("No answer from the server."));
  };

  const deleteRow = (id) => {
    fetch(`${API_URL}/api/products/${store}/${id}`, { method: "DELETE" }).then(() => fetchProducts());
  };

  const toggleSelect = (id) => {
    setSelected((prev) => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  };
  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((p) => p.id)));
  };
  const applyBatchAction = () => {
    if (selected.size === 0 || !batchAction) return;
    setBatchErr(""); // a new run clears the last run's message
    const ids = [...selected];
    if (batchAction === "delete") {
      ids.forEach((id) => fetch(`${API_URL}/api/products/${store}/${id}`, { method: "DELETE" }).catch(() => {}));
      setTimeout(() => fetchProducts(), 500);
    } else {
      const fieldMap = { baseProduct: "baseProduct", packSize: "packSize", mfgMethod: "manufacturingMethod", filamentGrams: "filamentGrams", shelfOnly: "shelfOnly" };
      const field = fieldMap[batchAction];
      // shelfOnly is the only boolean here — the picker sends "yes"/"no".
      const val = batchAction === "packSize" ? (batchValue || "1")
        : batchAction === "filamentGrams" ? (batchValue || "0")
        : batchAction === "shelfOnly" ? batchValue === "yes"
        : batchValue;
      // The server can refuse a row (a bundle cannot take a base product), so
      // read every answer. A swallowed 409 used to look like success here.
      Promise.all(ids.map((id) =>
        fetch(`${API_URL}/api/products/${store}/${id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ [field]: val }),
        })
          .then((r) => (r.ok ? null : r.json().then((d) => d.error || "The server refused this change.", () => "The server refused this change.")))
          .catch(() => "No answer from the server.")
      )).then((results) => {
        const failed = results.filter(Boolean);
        if (failed.length > 0) {
          setBatchErr(`${failed.length} of ${ids.length} did not save. ${failed[0]}`);
          setTimeout(() => setBatchErr(""), 8000);
        }
        fetchProducts();
      });
    }
    setSelected(new Set());
    setBatchAction(null);
    setBatchValue("");
  };

  // Etsy Import
  const [etsyOpen, setEtsyOpen] = useState(false);
  const [etsyLoading, setEtsyLoading] = useState(false);
  const [etsyErr, setEtsyErr] = useState("");
  const [etsyNoSku, setEtsyNoSku] = useState([]);
  const [etsyInvFailed, setEtsyInvFailed] = useState([]);
  const [etsyRows, setEtsyRows] = useState([]); // editable review rows
  const [etsyImporting, setEtsyImporting] = useState(false);

  const openEtsyImport = () => {
    setEtsyOpen(true); setEtsyLoading(true); setEtsyErr(""); setEtsyRows([]); setEtsyNoSku([]); setEtsyInvFailed([]);
    fetch(`${API_URL}/api/etsy/product-candidates?store=${store}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) { setEtsyErr(d.error); return; }
        const rows = [];
        (d.listings || []).forEach((li) => {
          // Group as a family only when the listing's variations look like pack
          // counts (3 Pack / Set of 5); size/color variations stay standalone.
          const isFamily = li.totalVariations > 1 && li.variations.some((v) => detectPackSize(v.label) > 1);
          li.variations.forEach((v, i) => {
            rows.push({
              key: `${li.listingId}-${i}`, listingId: li.listingId, listingTitle: li.title, label: v.label,
              checked: true, name: (li.title || "").slice(0, 200), sku: v.sku, price: v.price || li.price || 0,
              baseProduct: isFamily ? (li.title || "").slice(0, 200) : "",
              packSize: String(detectPackSize(v.label)), grams: "", method: "3D Printing",
            });
          });
        });
        setEtsyRows(rows);
        setEtsyNoSku(d.noSku || []);
        setEtsyInvFailed(d.inventoryFetchFailed || []);
      })
      .catch(() => setEtsyErr("Could not reach the server."))
      .finally(() => setEtsyLoading(false));
  };

  const setEtsyRow = (key, patch) => setEtsyRows((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const importEtsyRows = () => {
    const picked = etsyRows.filter((r) => r.checked);
    if (picked.length === 0 || etsyImporting) return;
    setEtsyImporting(true);
    const products = picked.map((r) => ({
      name: r.name, sku: r.sku, manufacturingMethod: r.method, price: r.price || "0", cost: "0",
      weightPacked: "", dimensionsPacked: "", baseProduct: r.baseProduct || "", packSize: r.packSize || "1",
      filamentGrams: r.grams || "0",
    }));
    fetch(`${API_URL}/api/products/batch`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store, products }),
    })
      .then((r) => r.json())
      .then((created) => {
        fetchProducts();
        setEtsyOpen(false);
        const count = Array.isArray(created) ? created.length : picked.length;
        setImportMsg(`Imported ${count} product${count !== 1 ? "s" : ""} from Etsy`);
        setTimeout(() => setImportMsg(""), 4000);
      })
      .catch(() => setEtsyErr("Import failed — is the server reachable?"))
      .finally(() => setEtsyImporting(false));
  };

  // CSV Import
  const fileInputRef = useRef(null);
  const [importMsg, setImportMsg] = useState("");
  const handleCsvImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) return;

      const header = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/['"]/g, ""));
      const colMap = {};
      const aliases = {
        name: ["name", "product", "product name", "title", "item"],
        sku: ["sku", "item number", "item no", "part number", "product sku"],
        manufacturingMethod: ["manufacturing method", "mfg method", "method", "manufacturing"],
        price: ["price", "retail price", "selling price"],
        cost: ["cost", "our cost", "unit cost", "cogs"],
        weightPacked: ["weight", "weight packed", "packed weight", "shipping weight"],
        dimensionsPacked: ["dimensions", "dimensions packed", "packed dimensions", "size"],
      };
      header.forEach((h, i) => {
        for (const [field, keys] of Object.entries(aliases)) {
          if (keys.some((k) => h.includes(k))) { colMap[field] = i; break; }
        }
      });

      const imported = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = [];
        let cur = "";
        let inQuote = false;
        for (const ch of lines[i]) {
          if (ch === '"') { inQuote = !inQuote; continue; }
          if (ch === "," && !inQuote) { vals.push(cur.trim()); cur = ""; continue; }
          cur += ch;
        }
        vals.push(cur.trim());

        const get = (field) => (colMap[field] !== undefined ? vals[colMap[field]] || "" : "");
        const name = get("name");
        if (!name) continue;

        imported.push({
          store,
          name,
          sku: get("sku"),
          manufacturingMethod: get("manufacturingMethod"),
          price: get("price").replace(/[$,]/g, "") || "0",
          cost: get("cost").replace(/[$,]/g, "") || "0",
          weightPacked: get("weightPacked"),
          dimensionsPacked: get("dimensionsPacked"),
        });
      }

      if (imported.length > 0) {
        fetch(`${API_URL}/api/products/batch`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ store, products: imported }),
        }).then((r) => r.json()).then((created) => {
          fetchProducts();
          const count = Array.isArray(created) ? created.length : imported.length;
          setImportMsg(`Imported ${count} product${count !== 1 ? "s" : ""}`);
          setTimeout(() => setImportMsg(""), 4000);
        }).catch(() => {
          setImportMsg("Import failed — check CSV format");
          setTimeout(() => setImportMsg(""), 4000);
        });
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const totalProducts = products.length;
  const avgPrice = totalProducts > 0 ? products.reduce((s, p) => s + (p.price || 0), 0) / totalProducts : 0;
  const avgCost = totalProducts > 0 ? products.reduce((s, p) => s + (p.cost || 0), 0) / totalProducts : 0;
  const avgMargin = avgPrice > 0 ? ((avgPrice - avgCost) / avgPrice) * 100 : 0;

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>{storeName} — Products</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Manage product catalog and pricing</p>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Total Products</span><span style={S.statValue}>{totalProducts}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Avg Price</span><span style={{ ...S.statValue, color: "#0033FF" }}>${avgPrice.toFixed(2)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Avg Cost</span><span style={S.statValue}>${avgCost.toFixed(2)}</span></div>
        <div style={S.statCard}><span style={S.statLabel}>Avg Margin</span><span style={{ ...S.statValue, color: "#34d399" }}>{avgMargin.toFixed(0)}%</span></div>
      </div>

      {/* Filter + Add */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search products..." style={{ ...S.input, marginBottom: 0, maxWidth: 280 }} />
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ color: "rgba(10,10,15,0.58)", fontSize: 15 }}>{totalProducts} product{totalProducts !== 1 ? "s" : ""}</span>
          <button onClick={openEtsyImport} style={{ ...S.addBtn, fontSize: 13 }} title="Find Etsy SKUs that aren't in the product database yet">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v8"/><path d="M8 12h8"/></svg>
            Import from Etsy
          </button>
          <button onClick={() => fileInputRef.current && fileInputRef.current.click()} style={{ ...S.addBtn, fontSize: 13 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            Import CSV
          </button>
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvImport} style={{ display: "none" }} />
          {!addingNew && <button onClick={() => setAddingNew(true)} style={S.addBtn}><PlusIcon /> Add Product</button>}
        </div>
      </div>
      {importMsg && <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 0, background: "rgba(34,197,94,0.1)", border: "1px solid #22c55e", color: "#22c55e", fontSize: 14, fontWeight: 600 }}>{importMsg}</div>}
      {batchErr && <div style={{ marginBottom: 16, padding: "10px 16px", borderRadius: 0, background: "rgba(239,68,68,0.1)", border: "1px solid #ef4444", color: "#ef4444", fontSize: 14, fontWeight: 600 }}>{batchErr}</div>}

      {/* Batch Action Bar */}
      {selected.size > 0 && (
        <div style={{
          background: "rgba(200,149,108,0.08)", border: "1px solid #0033FF", borderRadius: 0,
          padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "#0033FF" }}>{selected.size} selected</span>
          <div style={{ display: "flex", gap: 6, flex: 1, flexWrap: "wrap", alignItems: "center" }}>
            {isVV && (
              <button onClick={() => { setBatchAction("baseProduct"); setBatchValue(""); }} style={{
                padding: "6px 14px", borderRadius: 0, border: `1px solid ${batchAction === "baseProduct" ? "#0033FF" : "#0A0A0F"}`,
                background: batchAction === "baseProduct" ? "rgba(200,149,108,0.15)" : "transparent",
                color: batchAction === "baseProduct" ? "#0033FF" : "rgba(10,10,15,0.58)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
              }}>Set Base Product</button>
            )}
            {isVV && (
              <button onClick={() => { setBatchAction("packSize"); setBatchValue("1"); }} style={{
                padding: "6px 14px", borderRadius: 0, border: `1px solid ${batchAction === "packSize" ? "#0033FF" : "#0A0A0F"}`,
                background: batchAction === "packSize" ? "rgba(200,149,108,0.15)" : "transparent",
                color: batchAction === "packSize" ? "#0033FF" : "rgba(10,10,15,0.58)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
              }}>Set Pack Size</button>
            )}
            {isVV && (
              <button onClick={() => { setBatchAction("shelfOnly"); setBatchValue("yes"); }} style={{
                padding: "6px 14px", borderRadius: 0, border: `1px solid ${batchAction === "shelfOnly" ? "#0033FF" : "#0A0A0F"}`,
                background: batchAction === "shelfOnly" ? "rgba(200,149,108,0.15)" : "transparent",
                color: batchAction === "shelfOnly" ? "#0033FF" : "rgba(10,10,15,0.58)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
              }} title="A shelf row holds the family's count but is never sold">Shelf Row</button>
            )}
            {isVV && (
              <button onClick={() => { setBatchAction("filamentGrams"); setBatchValue(""); }} style={{
                padding: "6px 14px", borderRadius: 0, border: `1px solid ${batchAction === "filamentGrams" ? "#0033FF" : "#0A0A0F"}`,
                background: batchAction === "filamentGrams" ? "rgba(200,149,108,0.15)" : "transparent",
                color: batchAction === "filamentGrams" ? "#0033FF" : "rgba(10,10,15,0.58)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
              }}>Set Grams</button>
            )}
            <button onClick={() => { setBatchAction("mfgMethod"); setBatchValue(""); }} style={{
              padding: "6px 14px", borderRadius: 0, border: `1px solid ${batchAction === "mfgMethod" ? "#0033FF" : "#0A0A0F"}`,
              background: batchAction === "mfgMethod" ? "rgba(200,149,108,0.15)" : "transparent",
              color: batchAction === "mfgMethod" ? "#0033FF" : "rgba(10,10,15,0.58)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
            }}>Set Mfg Method</button>
            <button onClick={() => { setBatchAction("delete"); setBatchValue(""); }} style={{
              padding: "6px 14px", borderRadius: 0, border: "1px solid #ef4444",
              background: batchAction === "delete" ? "rgba(239,68,68,0.15)" : "transparent",
              color: "#ef4444", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
            }}>Delete</button>
          </div>
          {batchAction && batchAction !== "delete" && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {batchAction === "mfgMethod" ? (
                <select value={batchValue} onChange={(e) => setBatchValue(e.target.value)} style={{ ...S.input, marginBottom: 0, width: 180, fontSize: 13 }}>
                  <option value="">Select...</option>
                  {MFG_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              ) : batchAction === "baseProduct" ? (
                <input value={batchValue} onChange={(e) => setBatchValue(e.target.value)} placeholder="Base product name"
                  list="base-products-list"
                  style={{ ...S.input, marginBottom: 0, width: 200, fontSize: 13 }} />
              ) : batchAction === "shelfOnly" ? (
                <select value={batchValue} onChange={(e) => setBatchValue(e.target.value)} style={{ ...S.input, marginBottom: 0, width: 260, fontSize: 13 }}>
                  <option value="yes">Shelf row — holds the count, never sold</option>
                  <option value="no">Normal row — customers buy this</option>
                </select>
              ) : batchAction === "filamentGrams" ? (
                <input type="number" value={batchValue} onChange={(e) => setBatchValue(e.target.value)} placeholder="Grams / unit" step="0.1"
                  style={{ ...S.input, marginBottom: 0, width: 110, fontSize: 13, textAlign: "right" }} />
              ) : (
                <input value={batchValue} onChange={(e) => setBatchValue(e.target.value)} placeholder="Pack size"
                  style={{ ...S.input, marginBottom: 0, width: 80, fontSize: 13, textAlign: "center" }} />
              )}
              <button onClick={applyBatchAction} style={{
                padding: "8px 16px", borderRadius: 0, border: "none", fontSize: 13, fontWeight: 600,
                background: "#0033FF", color: "#FFFFFF", cursor: "pointer", fontFamily: "'Archivo', sans-serif",
              }}>Apply</button>
            </div>
          )}
          {batchAction === "delete" && (
            <button onClick={applyBatchAction} style={{
              padding: "8px 16px", borderRadius: 0, border: "none", fontSize: 13, fontWeight: 600,
              background: "#ef4444", color: "#fff", cursor: "pointer", fontFamily: "'Archivo', sans-serif",
            }}>Confirm Delete ({selected.size})</button>
          )}
          <button onClick={() => { setSelected(new Set()); setBatchAction(null); }} style={{
            padding: "6px 12px", borderRadius: 0, border: "2px solid #0A0A0F", background: "transparent",
            color: "rgba(10,10,15,0.58)", fontSize: 13, cursor: "pointer", fontFamily: "'Archivo', sans-serif",
          }}>Cancel</button>
          {/* Datalist for base product autocomplete */}
          {isVV && (
            <datalist id="base-products-list">
              {[...new Set(products.map((p) => p.baseProduct).filter(Boolean))].sort().map((bp) => (
                <option key={bp} value={bp} />
              ))}
            </datalist>
          )}
        </div>
      )}

      {/* Table */}
      <div style={{ ...S.tableWrap, overflowX: "auto" }}>
        <table style={{ ...S.table, minWidth: 900 }}>
          <thead>
            <tr>
              <th style={{ ...S.th, width: "3%", textAlign: "center" }}>
                <input type="checkbox" checked={selected.size > 0 && selected.size === filtered.length} onChange={selectAll} style={{ accentColor: "#0033FF", cursor: "pointer" }} />
              </th>
              <th onClick={() => handleSort("name")} style={{ ...S.th, width: "18%", cursor: "pointer", userSelect: "none" }}>Product Name<SortArrow sortCol={sortCol} col="name" sortDir={sortDir} /></th>
              <th onClick={() => handleSort("sku")} style={{ ...S.th, width: "10%", cursor: "pointer", userSelect: "none" }}>SKU<SortArrow sortCol={sortCol} col="sku" sortDir={sortDir} /></th>
              <th onClick={() => handleSort("manufacturingMethod")} style={{ ...S.th, width: "14%", cursor: "pointer", userSelect: "none" }}>Mfg Method<SortArrow sortCol={sortCol} col="manufacturingMethod" sortDir={sortDir} /></th>
              <th onClick={() => handleSort("price")} style={{ ...S.th, width: "10%", textAlign: "right", cursor: "pointer", userSelect: "none" }}>Price<SortArrow sortCol={sortCol} col="price" sortDir={sortDir} /></th>
              {isVV && <th style={{ ...S.th, width: "6%", textAlign: "right" }} title="Filament grams per single unit — for grouped products, set on the 1-pack variant">Grams</th>}
              <th onClick={() => handleSort("cost")} style={{ ...S.th, width: "10%", textAlign: "right", cursor: "pointer", userSelect: "none" }}>Cost<SortArrow sortCol={sortCol} col="cost" sortDir={sortDir} /></th>
              <th style={{ ...S.th, width: "7%", textAlign: "right" }}>Lbs</th>
              <th style={{ ...S.th, width: "7%", textAlign: "right" }}>Oz</th>
              <th style={{ ...S.th, width: isVV ? "10%" : "12%" }}>Dimensions</th>
              {isVV && <th onClick={() => handleSort("baseProduct")} style={{ ...S.th, width: "14%", cursor: "pointer", userSelect: "none" }}>Base Product<SortArrow sortCol={sortCol} col="baseProduct" sortDir={sortDir} /></th>}
              {isVV && <th style={{ ...S.th, width: "5%", textAlign: "center" }} title="How many units one sale of this row ships">Pack Qty</th>}
              <th style={{ ...S.th, width: "5%", textAlign: "right" }} title="Desired minimum stock — the print farm queues prints when the pool drops below this (set on the 1-pack variant for grouped products)">Min</th>
              <th style={{ ...S.th, width: "5%", textAlign: "right" }} title="Desired stock level the print farm builds up to">Target</th>
              <th style={{ ...S.th, width: isVV ? "8%" : "10%", textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {addingNew && (
              <tr style={S.addingRow}>
                <td style={S.td}></td>
                <td style={S.td}><CellInput value={newRow.name} onChange={(v) => setNewRow({ ...newRow, name: v })} placeholder="Product name" /></td>
                <td style={S.td}><CellInput value={newRow.sku} onChange={(v) => setNewRow({ ...newRow, sku: v })} placeholder="SKU" /></td>
                <td style={S.td}>
                  <select value={newRow.manufacturingMethod} onChange={(e) => setNewRow({ ...newRow, manufacturingMethod: e.target.value })} style={{ ...S.cellInput, fontSize: 14, cursor: "pointer" }}>
                    <option value="">Select...</option>
                    {MFG_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </td>
                <td style={S.td}><CellInput value={newRow.price} onChange={(v) => setNewRow({ ...newRow, price: v })} placeholder="0.00" style={{ textAlign: "right" }} /></td>
                {isVV && <td style={S.td}>{newRow.manufacturingMethod === "3D Printing" ? <CellInput value={newRow.filamentGrams} onChange={(v) => setNewRow({ ...newRow, filamentGrams: v })} placeholder="0" style={{ textAlign: "right" }} /> : <span style={{ color: "rgba(10,10,15,0.35)", display: "block", textAlign: "right" }} title="Grams only apply to 3D Printing products">—</span>}</td>}
                <td style={S.td}><CellInput value={newRow.cost} onChange={(v) => setNewRow({ ...newRow, cost: v })} placeholder="0.00" style={{ textAlign: "right" }} /></td>
                <td style={S.td}><CellInput value={newRow.weightLbs} onChange={(v) => setNewRow({ ...newRow, weightLbs: v })} placeholder="0" style={{ textAlign: "right" }} /></td>
                <td style={S.td}><CellInput value={newRow.weightOz} onChange={(v) => setNewRow({ ...newRow, weightOz: v })} placeholder="0" style={{ textAlign: "right" }} /></td>
                <td style={S.td}><CellInput value={newRow.dimensionsPacked} onChange={(v) => setNewRow({ ...newRow, dimensionsPacked: v })} placeholder='e.g. 6x4x2"' /></td>
                {isVV && <td style={S.td}><CellInput value={newRow.baseProduct} onChange={(v) => setNewRow({ ...newRow, baseProduct: v })} placeholder="Base product name" /></td>}
                {isVV && <td style={S.td}><CellInput value={newRow.packSize} onChange={(v) => setNewRow({ ...newRow, packSize: v })} placeholder="1" style={{ textAlign: "center", minWidth: 48, padding: "6px 6px" }} /></td>}
                <td style={S.td}><CellInput value={newRow.minInventory} onChange={(v) => setNewRow({ ...newRow, minInventory: v })} placeholder="—" style={{ textAlign: "right", minWidth: 44, padding: "6px 6px" }} /></td>
                <td style={S.td}><CellInput value={newRow.targetInventory} onChange={(v) => setNewRow({ ...newRow, targetInventory: v })} placeholder="—" style={{ textAlign: "right", minWidth: 44, padding: "6px 6px" }} /></td>
                <td style={{ ...S.td, textAlign: "right" }}>
                  <button onClick={addRow} style={S.iconBtn}><CheckIcon /></button>
                  <button onClick={() => { setAddingNew(false); setNewRow({ name: "", sku: "", manufacturingMethod: "", price: "", cost: "", weightLbs: "", weightOz: "", dimensionsPacked: "", baseProduct: "", packSize: "1", filamentGrams: "", minInventory: "", targetInventory: "" }); }} style={{ ...S.iconBtn, marginLeft: 6 }}><XIcon /></button>
                </td>
              </tr>
            )}
            {(() => {
              const renderRow = (p, indent) => {
                const parts = hwByProduct[p.id] || [];
                const partsOpen = expandedParts.has(p.id);
                return (
                <React.Fragment key={p.id}>
                <tr style={editId === p.id ? S.editingRow : {}}>
                  <td style={{ ...S.td, textAlign: "center" }}>
                    <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} style={{ cursor: "pointer" }} />
                  </td>
                  {editId === p.id ? (
                    <>
                      <td style={S.td}><CellInput value={editRow.name} onChange={(v) => setEditRow({ ...editRow, name: v })} /></td>
                      <td style={S.td}><CellInput value={editRow.sku} onChange={(v) => setEditRow({ ...editRow, sku: v })} /></td>
                      <td style={S.td}>
                        <select value={editRow.manufacturingMethod} onChange={(e) => setEditRow({ ...editRow, manufacturingMethod: e.target.value })} style={{ ...S.cellInput, fontSize: 14, cursor: "pointer" }}>
                          <option value="">Select...</option>
                          {MFG_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                      </td>
                      <td style={S.td}><CellInput value={editRow.price} onChange={(v) => setEditRow({ ...editRow, price: v })} style={{ textAlign: "right" }} /></td>
                      {isVV && <td style={S.td}>{editRow.manufacturingMethod === "3D Printing" ? <CellInput value={String(editRow.filamentGrams ?? "")} onChange={(v) => setEditRow({ ...editRow, filamentGrams: v })} placeholder="0" style={{ textAlign: "right" }} /> : <span style={{ color: "rgba(10,10,15,0.35)", display: "block", textAlign: "right" }} title="Grams only apply to 3D Printing products">—</span>}</td>}
                      <td style={S.td}><CellInput value={editRow.cost} onChange={(v) => setEditRow({ ...editRow, cost: v })} style={{ textAlign: "right" }} /></td>
                      <td style={S.td}><CellInput value={editRow.weightLbs} onChange={(v) => setEditRow({ ...editRow, weightLbs: v })} style={{ textAlign: "right" }} /></td>
                      <td style={S.td}><CellInput value={editRow.weightOz} onChange={(v) => setEditRow({ ...editRow, weightOz: v })} style={{ textAlign: "right" }} /></td>
                      <td style={S.td}><CellInput value={editRow.dimensionsPacked} onChange={(v) => setEditRow({ ...editRow, dimensionsPacked: v })} /></td>
                      {isVV && <td style={S.td}><CellInput value={editRow.baseProduct || ""} onChange={(v) => setEditRow({ ...editRow, baseProduct: v })} /></td>}
                      {isVV && <td style={S.td}><CellInput value={String(editRow.packSize || 1)} onChange={(v) => setEditRow({ ...editRow, packSize: v })} style={{ textAlign: "center", minWidth: 48, padding: "6px 6px" }} /></td>}
                      <td style={S.td}><CellInput value={editRow.minInventory ?? ""} onChange={(v) => setEditRow({ ...editRow, minInventory: v })} placeholder="—" style={{ textAlign: "right", minWidth: 44, padding: "6px 6px" }} /></td>
                      <td style={S.td}><CellInput value={editRow.targetInventory ?? ""} onChange={(v) => setEditRow({ ...editRow, targetInventory: v })} placeholder="—" style={{ textAlign: "right", minWidth: 44, padding: "6px 6px" }} /></td>
                      <td style={{ ...S.td, textAlign: "right" }}>
                        <button onClick={saveEdit} style={S.iconBtn}><CheckIcon /></button>
                        <button onClick={() => setEditId(null)} style={{ ...S.iconBtn, marginLeft: 6 }}><XIcon /></button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ ...S.td, fontWeight: 600, color: "#0A0A0F", paddingLeft: indent ? 28 : undefined }}>
                        {indent && <span style={{ color: "#0A0A0F", marginRight: 6 }}>└</span>}{p.name}
                        {p.isBundle && (
                          <span style={{ ...S.typeBadge, marginLeft: 8, borderColor: "#0033FF", color: "#0033FF" }}>Bundle</span>
                        )}
                        {p.shelfOnly && (
                          <span style={{ ...S.typeBadge, marginLeft: 8, borderColor: "#8A6206", color: "#8A6206" }} title="Holds this family's count. Never sold, and hidden on the Inventory page.">Shelf row</span>
                        )}
                        {parts.length > 0 && (
                          // Flat one-liner under the name. The old bordered box
                          // read as a stray table cell and doubled the row height
                          // whenever the name wrapped (Daniel: "seems messy").
                          <button
                            onClick={() => toggleParts(p.id)}
                            title="What goes into one of these"
                            style={{ display: "block", background: "none", border: "none", padding: 0, marginTop: 3, cursor: "pointer", fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: partsOpen ? "#0033FF" : "rgba(10,10,15,0.45)", fontFamily: "'JetBrains Mono', monospace" }}
                          >
                            {partsOpen ? "▾" : "▸"} Parts & hardware · {parts.length}
                          </button>
                        )}
                      </td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{p.sku || "—"}</td>
                      <td style={S.td}>
                        {p.manufacturingMethod ? (
                          <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 0, fontSize: 13, fontWeight: 600, background: "#F4F2EC", color: "#0A0A0F" }}>{p.manufacturingMethod}</span>
                        ) : "—"}
                      </td>
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0033FF", fontWeight: 600 }}>${(p.price || 0).toFixed(2)}</td>
                      {isVV && (() => {
                        // Pack variants inherit grams from the 1-pack (inventory holder) variant.
                        // Grams only apply to 3D Printing products.
                        const holder = p.baseProduct ? products.find((q) => q.baseProduct === p.baseProduct && (q.packSize || 1) === 1) : null;
                        const isInherited = holder && holder.id !== p.id;
                        const is3DP = ((holder || p).manufacturingMethod === "3D Printing") || (p.manufacturingMethod === "3D Printing");
                        const shown = is3DP ? (isInherited ? holder.filamentGrams || 0 : p.filamentGrams || 0) : 0;
                        return <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "right", color: isInherited ? "rgba(10,10,15,0.45)" : "#0A0A0F" }} title={isInherited ? "Inherited from the 1-pack variant" : !is3DP ? "Grams only apply to 3D Printing products" : undefined}>{shown > 0 ? `${shown}g` : "—"}</td>;
                      })()}
                      <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 15, textAlign: "right", color: "#0A0A0F" }}>${(p.cost || 0).toFixed(2)}</td>
                      {(() => { const { lbs, oz } = parseWeight(p.weightPacked); return (<>
                        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "right", color: "#0A0A0F" }}>{lbs || "—"}</td>
                        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "right", color: "#0A0A0F" }}>{oz || "—"}</td>
                      </>); })()}
                      <td style={{ ...S.td, fontSize: 14, color: "#0A0A0F" }}>{p.dimensionsPacked || "—"}</td>
                      {isVV && <td style={{ ...S.td, fontSize: 13, color: p.baseProduct ? "#0033FF" : "rgba(10,10,15,0.58)" }}>{p.baseProduct || "—"}</td>}
                      {isVV && <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "center", color: (p.packSize || 1) > 1 ? "#0033FF" : "#0A0A0F" }}>{p.packSize || 1}</td>}
                      {(() => {
                        // Inventory levels live on the 1-pack (inventory holder) variant, like grams.
                        const lvlHolder = p.baseProduct ? products.find((q) => q.baseProduct === p.baseProduct && (q.packSize || 1) === 1) : null;
                        const lvlInherited = lvlHolder && lvlHolder.id !== p.id;
                        const src = lvlInherited ? lvlHolder : p;
                        const c = lvlInherited ? "rgba(10,10,15,0.45)" : "#0A0A0F";
                        const ttl = lvlInherited ? "Inherited from the 1-pack variant" : undefined;
                        return (<>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "right", color: c }} title={ttl}>{src.minInventory ?? "—"}</td>
                          <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, textAlign: "right", color: src.targetInventory != null ? "#0033FF" : c }} title={ttl}>{src.targetInventory ?? "—"}</td>
                        </>);
                      })()}
                      <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          onClick={() => setBundling(p)}
                          title="Set what is inside this product"
                          style={{ ...S.iconBtn, marginRight: 6, width: "auto", padding: "0 8px", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", borderColor: p.isBundle ? "#0033FF" : "#0A0A0F", color: p.isBundle ? "#0033FF" : "#0A0A0F" }}
                        >
                          {p.isBundle ? `${p.bundleMemberCount} IN IT` : "BUNDLE"}
                        </button>
                        <button onClick={() => startEdit(p)} style={S.iconBtn}><EditIcon /></button>
                        <button onClick={() => deleteRow(p.id)} style={{ ...S.iconBtn, marginLeft: 6 }}><TrashIcon /></button>
                      </td>
                    </>
                  )}
                </tr>
                {editId === p.id && editErr && (
                  <tr>
                    <td colSpan={isVV ? 15 : 12} style={{ ...S.td, background: "rgba(239,68,68,0.08)", color: "#ef4444", fontSize: 13, fontWeight: 600 }}>{editErr}</td>
                  </tr>
                )}
                {partsOpen && <PartsAndHardwareRow entries={parts} colSpan={isVV ? 15 : 12} />}
                </React.Fragment>
                );
              };

              if (!isVV) return filtered.map((p) => renderRow(p, false));

              // Group by baseProduct for VV
              const groups = {};
              const standalone = [];
              filtered.forEach((p) => {
                if (p.baseProduct) {
                  if (!groups[p.baseProduct]) groups[p.baseProduct] = [];
                  groups[p.baseProduct].push(p);
                } else {
                  standalone.push(p);
                }
              });

              const rows = [];
              // Render grouped products
              Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0])).forEach(([bp, items]) => {
                const isOpen = expandedGroups.has(bp);
                const colSpan = isVV ? 15 : 12;
                rows.push(
                  <tr key={`group-${bp}`} onClick={() => toggleGroup(bp)} style={{ cursor: "pointer", background: "rgba(200,149,108,0.04)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(200,149,108,0.08)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(200,149,108,0.04)")}
                  >
                    <td style={{ ...S.td, textAlign: "center" }}>
                      <input type="checkbox"
                        checked={items.every((p) => selected.has(p.id))}
                        onChange={(e) => { e.stopPropagation(); const ids = items.map((p) => p.id); setSelected((prev) => { const s = new Set(prev); if (items.every((p) => s.has(p.id))) ids.forEach((id) => s.delete(id)); else ids.forEach((id) => s.add(id)); return s; }); }}
                        style={{ cursor: "pointer" }} />
                    </td>
                    <td colSpan={colSpan - 1} style={{ ...S.td, padding: "10px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0033FF" strokeWidth="2.5" strokeLinecap="round"
                          style={{ transform: isOpen ? "rotate(90deg)" : "rotate(0deg)", transition: "transform .15s" }}>
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#0033FF" }}>{bp}</span>
                        <span style={{ fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: "rgba(10,10,15,0.58)", background: "#F4F2EC", padding: "1px 8px", borderRadius: 0}}>
                          {items.length} variant{items.length !== 1 ? "s" : ""}
                        </span>
                        <span style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>
                          Pack sizes: {items.map((p) => p.packSize || 1).sort((a, b) => a - b).join(", ")}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
                if (isOpen) {
                  items.forEach((p) => rows.push(renderRow(p, true)));
                }
              });
              // Render standalone products
              standalone.forEach((p) => rows.push(renderRow(p, false)));
              return rows;
            })()}
            {filtered.length === 0 && !addingNew && (
              <tr><td colSpan={isVV ? 15 : 12} style={{ ...S.td, textAlign: "center", color: "rgba(10,10,15,0.58)", padding: 40 }}>{filter ? "No products match." : "No products yet. Click Add Product to start."}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Import from Etsy modal */}
      {etsyOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(10,10,15,0.55)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={(e) => { if (e.target === e.currentTarget && !etsyImporting) setEtsyOpen(false); }}>
          <div style={{ background: "#F4F2EC", border: "3px solid #0A0A0F", width: "min(1100px, 96vw)", maxHeight: "88vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "16px 20px", borderBottom: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <div>
                <div style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 20, fontWeight: 900, color: "#0A0A0F", textTransform: "uppercase", letterSpacing: "-0.01em" }}>Import from Etsy</div>
                <div style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", marginTop: 2 }}>
                  {etsyLoading ? "Checking your Etsy shop for new SKUs…" : etsyErr ? "" : `${etsyRows.length} new SKU${etsyRows.length !== 1 ? "s" : ""} not in the product database`}
                </div>
              </div>
              <button onClick={() => setEtsyOpen(false)} disabled={etsyImporting} style={{ ...S.iconBtn, fontSize: 16 }}><XIcon /></button>
            </div>

            <div style={{ overflowY: "auto", overflowX: "auto", flex: 1, padding: "0 0 8px" }}>
              {etsyLoading && <div style={{ padding: 40, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>Loading listings from Etsy…</div>}
              {etsyErr && <div style={{ margin: 20, padding: "10px 16px", background: "rgba(239,68,68,0.08)", border: "1px solid #ef4444", color: "#ef4444", fontSize: 14, fontWeight: 600 }}>{etsyErr}</div>}
              {!etsyLoading && !etsyErr && etsyRows.length === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>Every Etsy SKU is already in the product database. ✓</div>
              )}

              {etsyRows.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width: 36, textAlign: "center" }}>
                        <input type="checkbox" checked={etsyRows.every((r) => r.checked)} onChange={(e) => setEtsyRows((rows) => rows.map((r) => ({ ...r, checked: e.target.checked })))} style={{ accentColor: "#0033FF", cursor: "pointer" }} />
                      </th>
                      <th style={{ ...S.th, minWidth: 220 }}>Product Name</th>
                      <th style={{ ...S.th, minWidth: 110 }}>Variation</th>
                      <th style={{ ...S.th, minWidth: 110 }}>SKU</th>
                      <th style={{ ...S.th, width: 80, textAlign: "right" }}>Price</th>
                      <th style={{ ...S.th, minWidth: 160 }}>Base Product</th>
                      <th style={{ ...S.th, width: 90, textAlign: "center" }} title="How many units one sale of this row ships">Pack Qty</th>
                      <th style={{ ...S.th, width: 90, textAlign: "right" }}>Grams</th>
                      <th style={{ ...S.th, minWidth: 130 }}>Mfg Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    {etsyRows.map((r, i) => {
                      const firstOfListing = i === 0 || etsyRows[i - 1].listingId !== r.listingId;
                      return (
                        <React.Fragment key={r.key}>
                          {firstOfListing && (
                            <tr>
                              <td colSpan={11} style={{ padding: "10px 12px 4px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, color: "#0033FF", borderBottom: "1px solid rgba(10,10,15,0.12)", background: "rgba(0,51,255,0.04)" }}>
                                {r.listingTitle}
                              </td>
                            </tr>
                          )}
                          <tr style={{ borderBottom: "1px solid rgba(10,10,15,0.1)", opacity: r.checked ? 1 : 0.45 }}>
                            <td style={{ ...S.td, textAlign: "center" }}>
                              <input type="checkbox" checked={r.checked} onChange={(e) => setEtsyRow(r.key, { checked: e.target.checked })} style={{ accentColor: "#0033FF", cursor: "pointer" }} />
                            </td>
                            <td style={S.td}><CellInput value={r.name} onChange={(v) => setEtsyRow(r.key, { name: v })} /></td>
                            <td style={{ ...S.td, fontSize: 13, color: "rgba(10,10,15,0.58)" }}>{r.label || "—"}</td>
                            <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}>{r.sku}</td>
                            <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace", fontSize: 13, textAlign: "right" }}>${(parseFloat(r.price) || 0).toFixed(2)}</td>
                            <td style={S.td}><CellInput value={r.baseProduct} onChange={(v) => setEtsyRow(r.key, { baseProduct: v })} placeholder="(standalone)" /></td>
                            <td style={S.td}><CellInput value={r.packSize} onChange={(v) => setEtsyRow(r.key, { packSize: v })} style={{ textAlign: "center", minWidth: 56, padding: "6px 8px" }} /></td>
                            <td style={S.td}>{r.method === "3D Printing" ? <CellInput value={r.grams} onChange={(v) => setEtsyRow(r.key, { grams: v })} placeholder="0" style={{ textAlign: "right" }} /> : <span style={{ color: "rgba(10,10,15,0.35)", display: "block", textAlign: "right" }}>—</span>}</td>
                            <td style={S.td}>
                              <select value={r.method} onChange={(e) => setEtsyRow(r.key, { method: e.target.value })} style={{ ...S.cellInput, fontSize: 13, cursor: "pointer" }}>
                                {MFG_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                              </select>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {etsyNoSku.length > 0 && (
                <div style={{ margin: "14px 20px 6px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#f59e0b", marginBottom: 4 }}>⚠ Listings with no SKU on Etsy ({etsyNoSku.length}) — add SKUs on Etsy first, then re-open this import</div>
                  {etsyNoSku.map((l) => (
                    <div key={l.listingId} style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", lineHeight: 1.7 }}>• {l.title}</div>
                  ))}
                </div>
              )}
              {etsyInvFailed.length > 0 && (
                <div style={{ margin: "14px 20px 6px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "#ef4444", marginBottom: 4 }}>⚠ Couldn't verify variations for {etsyInvFailed.length} listing{etsyInvFailed.length !== 1 ? "s" : ""} (Etsy API hiccup) — these may show fewer SKUs than they really have. Close and re-open to retry.</div>
                  {etsyInvFailed.map((l) => (
                    <div key={l.listingId} style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", lineHeight: 1.7 }}>• {l.title}</div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ padding: "12px 20px", borderTop: "2px solid #0A0A0F", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)" }}>
                {etsyRows.filter((r) => r.checked).length} of {etsyRows.length} selected — grams can be left blank and filled later
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setEtsyOpen(false)} disabled={etsyImporting} style={S.clearBtn}>Cancel</button>
                <button onClick={importEtsyRows} disabled={etsyImporting || etsyRows.filter((r) => r.checked).length === 0}
                  style={{ ...S.saveJobBtn, opacity: etsyImporting || etsyRows.filter((r) => r.checked).length === 0 ? 0.5 : 1 }}>
                  {etsyImporting ? "Importing…" : `Import ${etsyRows.filter((r) => r.checked).length} Product${etsyRows.filter((r) => r.checked).length !== 1 ? "s" : ""}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {bundling && (
        <EditBundleModal
          product={bundling}
          store={store}
          storeName={storeName}
          onClose={() => setBundling(null)}
          onSaved={() => fetchProducts()}
        />
      )}
    </div>
  );
}
